import { useEffect, useMemo, useState, useRef } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { MobileActionBar } from "../components/MobileActionBar";
import {
  MapPin, Clock, Package, Lock, CheckCircle2, ShieldCheck, Loader2, Trophy, Star, AlertCircle, ReceiptText, MessageSquare,
} from "lucide-react";
import { Badge } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useListing } from "../lib/useListing";
import { rfqs as mockRfqs, peso } from "../data/mock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const CAN_QUOTE = ["offer_service", "promote_company"];
const money = (s: string) => Number(String(s).replace(/[^\d]/g, "")) || 0;

type Item = { name: string; qty: number; unit: string };

export default function RfqDetail() {
  const [, params] = useRoute("/rfq/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const { item: rfq, state } = useListing("rfqs", id, mockRfqs as any);

  const [qData, setQData] = useState<Awaited<ReturnType<typeof api.getQuotes>>>(null as any);
  const [prices, setPrices] = useState<string[]>([]);
  const [meta, setMeta] = useState({ supplierName: "", validityText: "", notes: "", company: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const quoteRef = useRef<HTMLDivElement>(null);

  const items: Item[] = useMemo(() => (Array.isArray(rfq?.items) ? rfq.items : []), [rfq]);
  const detail = (rfq?.detail && typeof rfq.detail === "object") ? rfq.detail : {};
  const canQuote = !!user && CAN_QUOTE.some((c) => user.capabilities?.includes(c));

  async function loadQuotes() {
    if (!user) { setQData(null as any); return; }
    try { setQData(await api.getQuotes(id)); } catch { /* keep */ }
  }
  useEffect(() => { loadQuotes(); /* eslint-disable-next-line */ }, [id, user?.id]);
  useEffect(() => { if (items.length && prices.length !== items.length) setPrices(Array(items.length).fill("")); /* eslint-disable-next-line */ }, [items.length]);

  if (state === "loading") return <DetailLoading label="Loading RFQ…" />;
  if (state === "notfound" || !rfq) return <DetailNotFound title="RFQ not found" backHref="/marketplace?tab=RFQs" backLabel="Browse RFQs" />;

  const isOwner = !!qData?.isOwner;
  const myQuote = (qData as any)?.myQuote || null;
  const awarded = rfq.status === "awarded" || qData?.status === "awarded";
  const liveTotal = items.reduce((sum, it, i) => sum + (Number(it.qty) || 0) * money(prices[i] || ""), 0);

  async function submitQuote(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (prices.some((p) => money(p) <= 0)) { setErr("Enter a unit price for every line item."); return; }
    setBusy(true);
    try {
      await api.submitQuote(id, {
        lines: items.map((_, i) => ({ unitPrice: money(prices[i]) })),
        notes: meta.notes || undefined,
        validityText: meta.validityText || undefined,
        company: meta.company,
        supplierName: meta.supplierName || undefined,
      });
      await loadQuotes();
    } catch (e: any) { setErr(e?.message || "Could not submit your quote."); }
    finally { setBusy(false); }
  }
  async function shortlist(qid: string) {
    setBusy(true);
    try { await api.shortlistQuote(id, qid); await loadQuotes(); } catch (e: any) { setErr(e?.message || "Action failed."); } finally { setBusy(false); }
  }
  async function award(qid: string) {
    setBusy(true);
    try { await api.awardQuote(id, qid); await loadQuotes(); } catch (e: any) { setErr(e?.message || "Could not award."); } finally { setBusy(false); }
  }

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <div className="text-sm text-mut mb-4">
        <Link href="/marketplace?tab=RFQs" className="hover:text-navy">RFQs</Link> / {rfq.category} / Request for Quotation
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div className="space-y-4">
          <div className="card p-6">
            {awarded
              ? <span className="chip bg-brand-gold/15 text-[#8a5a00] mb-2">Awarded</span>
              : <span className="chip bg-brand-green/10 text-brand-greendark mb-2">Open for Quotes</span>}
            <h1 className="text-2xl font-extrabold text-navy">{rfq.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-mut">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{rfq.location}</span>
              <span>Category: {rfq.category}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" />Due: {rfq.deadline}</span>
              <span>Posted by {rfq.owner}</span>
            </div>
            {detail.description && <p className="text-sm text-mut mt-3">{detail.description}</p>}
            {user && rfq.ownerUserId != null && rfq.ownerUserId !== user.id && (
              <button onClick={() => navigate(`/messages?to=${rfq.ownerUserId}&type=rfq&id=${encodeURIComponent(id)}&name=${encodeURIComponent(rfq.owner)}`)} className="btn-outline h-9 px-3 text-xs mt-3"><MessageSquare className="w-4 h-4" />Message buyer</button>
            )}
          </div>

          {/* Line items */}
          <div className="card p-6">
            <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Package className="w-4 h-4" />Requested Items ({items.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-mut border-b border-line">
                    <th className="py-2 pr-3 font-semibold">#</th>
                    <th className="py-2 px-3 font-semibold">Item</th>
                    <th className="py-2 px-3 font-semibold text-right">Qty</th>
                    <th className="py-2 pl-3 font-semibold">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-b border-line/60">
                      <td className="py-2 pr-3 text-mut">{i + 1}</td>
                      <td className="py-2 px-3 font-medium text-navy">{it.name}</td>
                      <td className="py-2 px-3 text-right text-navy">{it.qty}</td>
                      <td className="py-2 pl-3 text-mut">{it.unit || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {detail.terms && <p className="text-xs text-mut mt-3">Terms: {detail.terms}</p>}
          </div>

          {/* Owner: quote comparison */}
          {isOwner && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-navy">Quotes Received ({qData?.total ?? 0})</h3>
                {awarded && <span className="chip bg-brand-gold/15 text-[#8a5a00]">Awarded</span>}
              </div>
              {err && <div className="mb-3 text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</div>}
              {(qData?.quotes?.length ?? 0) === 0 ? (
                <p className="text-sm text-mut">No quotes yet. Supplier prices are sealed — you&apos;ll see each total here, cheapest first.</p>
              ) : (
                <div className="space-y-3">
                  {qData!.quotes.map((q: any) => (
                    <div key={q.id} className={`border rounded-lg p-3 ${q.status === "awarded" ? "border-brand-gold bg-brand-gold/5" : "border-line"}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-semibold text-navy">{q.supplierName} {q.company && <span className="text-[11px] text-mut">· Company</span>}</div>
                          {q.validityText && <div className="text-[11px] text-mut">Valid: {q.validityText}</div>}
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-extrabold text-navy">{peso(q.total)}</div>
                          <div className="text-[11px]">
                            {q.status === "awarded" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">Awarded</span>}
                            {q.status === "shortlisted" && <span className="chip bg-brand-green/10 text-brand-greendark">Shortlisted</span>}
                            {q.status === "submitted" && <span className="chip bg-navy-50 text-navy">Submitted</span>}
                            {q.status === "rejected" && <span className="chip bg-line text-mut">Not selected</span>}
                          </div>
                        </div>
                      </div>
                      {/* per-line breakdown */}
                      <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                        {items.map((it, i) => (
                          <div key={i} className="flex justify-between text-xs text-mut">
                            <span className="truncate">{it.name} <span className="text-navy/50">×{it.qty}</span></span>
                            <span className="text-navy font-medium">{peso(Number(q.lines?.[i]?.unitPrice) || 0)}/{it.unit || "unit"}</span>
                          </div>
                        ))}
                      </div>
                      {q.notes && <div className="text-xs text-mut mt-2 border-t border-line pt-2">{q.notes}</div>}
                      {!awarded && (
                        <div className="flex gap-2 mt-3 justify-end">
                          <button disabled={busy} onClick={() => shortlist(q.id)} className="btn-outline h-8 px-2.5 text-xs"><Star className="w-3.5 h-3.5" />{q.status === "shortlisted" ? "Unshortlist" : "Shortlist"}</button>
                          <button disabled={busy} onClick={() => award(q.id)} className="btn-red h-8 px-2.5 text-xs"><Trophy className="w-3.5 h-3.5" />Award {peso(q.total)}</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="font-bold text-navy mb-3">RFQ Summary</h3>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-mut">Status</dt><dd className="font-semibold text-navy">{awarded ? "Awarded" : "Open"}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Line items</dt><dd className="font-semibold text-navy">{items.length}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Quotes received</dt><dd className="font-semibold text-navy">{rfq.quotes ?? 0}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Closes</dt><dd className="font-semibold text-navy">{rfq.deadline}</dd></div>
            </dl>
          </div>

          {!isOwner && (
            <div ref={quoteRef} className="card p-5">
              <div className="flex items-center gap-2 mb-1"><Lock className="w-4 h-4 text-brand-green" /><h3 className="font-bold text-navy">Submit Your Quote</h3></div>
              <p className="text-xs text-mut mb-4">Your prices are sealed — hidden from other suppliers.</p>

              {awarded ? (
                <div className="text-center py-6">
                  <Trophy className="w-10 h-10 text-brand-gold mx-auto mb-2" />
                  <div className="font-bold text-navy">Quoting closed</div>
                  <p className="text-xs text-mut mt-1">{myQuote?.status === "awarded" ? "Congratulations — your quote was awarded." : "This RFQ has been awarded."}</p>
                </div>
              ) : !user ? (
                <div className="text-center py-4">
                  <p className="text-sm text-mut mb-3">Sign in to submit a quote.</p>
                  <button onClick={() => navigate("/login")} className="btn-red w-full">Sign in to quote</button>
                </div>
              ) : !canQuote ? (
                <div className="text-center py-4">
                  <AlertCircle className="w-7 h-7 text-brand-gold mx-auto mb-2" />
                  <p className="text-sm text-mut mb-3">Quoting needs the “Offer a Service” or “Promote a Company” activity enabled.</p>
                  <button onClick={() => navigate("/dashboard")} className="btn-outline w-full">Manage activities</button>
                </div>
              ) : myQuote ? (
                <div>
                  <div className="rounded-lg border border-brand-green/30 bg-brand-green/5 p-3 mb-3 text-center">
                    <CheckCircle2 className="w-8 h-8 text-brand-green mx-auto mb-1" />
                    <div className="font-bold text-navy text-sm">Your sealed quote is in</div>
                    <div className="text-lg font-extrabold text-navy mt-1">{peso(myQuote.total)}</div>
                    <div className="text-xs text-mut">{myQuote.status === "shortlisted" ? "Shortlisted by buyer" : "Submitted"}</div>
                  </div>
                  {err && <div className="mb-2 text-xs text-brand-red text-center">{err}</div>}
                  <details>
                    <summary className="text-xs font-semibold text-navy cursor-pointer select-none">Revise my quote</summary>
                    <QuoteForm items={items} prices={prices} setPrices={setPrices} meta={meta} setMeta={setMeta} liveTotal={liveTotal} busy={busy} onSubmit={submitQuote} label="Update Quote" />
                  </details>
                </div>
              ) : (
                <>
                  {err && <div className="mb-2 text-xs text-brand-red text-center flex items-center gap-1 justify-center"><AlertCircle className="w-3.5 h-3.5" />{err}</div>}
                  <QuoteForm items={items} prices={prices} setPrices={setPrices} meta={meta} setMeta={setMeta} liveTotal={liveTotal} busy={busy} onSubmit={submitQuote} label="Submit Sealed Quote" />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {!isOwner && !awarded && (
        <MobileActionBar>
          <div className="shrink-0">
            <div className="text-[11px] text-mut leading-none">RFQ</div>
            <div className="text-sm font-extrabold text-navy leading-tight">{items.length} item{items.length === 1 ? "" : "s"}</div>
          </div>
          <button type="button" onClick={() => quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="btn-red flex-1"><ReceiptText className="w-4 h-4" />{myQuote ? "View my quote" : "Submit a Quote"}</button>
        </MobileActionBar>
      )}
    </div>
  );
}

function QuoteForm({
  items, prices, setPrices, meta, setMeta, liveTotal, busy, onSubmit, label,
}: {
  items: Item[]; prices: string[]; setPrices: (p: string[]) => void;
  meta: { supplierName: string; validityText: string; notes: string; company: boolean };
  setMeta: (m: any) => void; liveTotal: number; busy: boolean; onSubmit: (e: React.FormEvent) => void; label: string;
}) {
  const setPrice = (i: number, v: string) => setPrices(prices.map((p, j) => (j === i ? v : p)));
  const setM = (k: string, v: any) => setMeta((m: any) => ({ ...m, [k]: v }));
  return (
    <form onSubmit={onSubmit} className="space-y-3 mt-3">
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i}>
            <label className="label">{it.name} <span className="text-mut font-normal">({it.qty} {it.unit || "unit"})</span></label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-mut text-sm">₱</span>
              <input className="field pl-7" inputMode="numeric" value={prices[i] || ""} onChange={(e) => setPrice(i, e.target.value)} placeholder="unit price" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between items-center rounded-lg bg-navy-50 px-3 py-2">
        <span className="text-xs font-semibold text-navy">Quote total</span>
        <span className="text-lg font-extrabold text-navy">{peso(liveTotal)}</span>
      </div>
      <div><label className="label">Company / Supplier</label><input className="field" value={meta.supplierName} onChange={(e) => setM("supplierName", e.target.value)} placeholder="Your company name" /></div>
      <div><label className="label">Validity</label><input className="field" value={meta.validityText} onChange={(e) => setM("validityText", e.target.value)} placeholder="e.g. 30 days" /></div>
      <div><label className="label">Notes</label><textarea className="field h-16 py-2" value={meta.notes} onChange={(e) => setM("notes", e.target.value)} placeholder="Inclusions, delivery, warranty…" /></div>
      <label className="flex items-center gap-2 text-xs text-mut"><input type="checkbox" checked={meta.company} onChange={(e) => setM("company", e.target.checked)} />Quoting as a registered company</label>
      <button disabled={busy} className="btn-red w-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptText className="w-4 h-4" />}{label}</button>
      <p className="text-[11px] text-mut flex items-center gap-1 justify-center"><ShieldCheck className="w-3 h-3" />Sealed until the buyer reviews</p>
    </form>
  );
}
